import { apiService, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { apiHosts } from "@/api/constants/apiHosts";
import { NotifyOptionsError, NotifyOptionsSuccess } from "@modernary/mui/components/notifications";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import type { AxiosResponse } from "axios";
import type { Organization, OrganizationNew, OrganizationQuery } from "./organization";
import organizationService from "./organizationService";
import { LE } from "@/constants/enums";

const URL = "organizations";
const ENTITY_NAME = "Organization";

const axiosParams = {
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<Organization>(apiHosts.AQMS, axiosParams);

const getOrganizations = (params: OrganizationQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

type OrganizationIds = {
  id?: string | number;
};
const getNewOrganization = async (ids: OrganizationIds) => {
  const defaultApp = {
    data: {
      ...organizationService.getInitialData(),
      ...ids,
    },
  } as AxiosResponse<OrganizationNew, any>;

  return defaultApp;
};

const getOrganizationById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const createOrganization = (organization: Organization, config: PagingApiAndNotifierConfig) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };
  return api.post(URL, organization, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateOrganization = (organization: Organization, config: PagingApiAndNotifierConfig) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };
  return api.put(`${URL}/${organization.id}`, organization, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {Organization} organization
 */
const saveOrganization = (organization: Organization, config = {}) => {
  if (organization.id) {
    return updateOrganization(organization, config);
  } else {
    return createOrganization(organization, config);
  }
};

const deleteOrganizationById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };
  return api.delete(`${URL}/${id}`, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  getOrganizations,
  getNewOrganization,
  getOrganizationById,
  createOrganization,
  updateOrganization,
  saveOrganization,
  deleteOrganizationById,
};
